/* ============================================================
   ENSSA copy / vocabulary glossary
   Single source for risk labels, completion-status labels, and
   forbidden words. Components must consume from here.

   Note: this file is the ONLY place where forbidden words appear
   as data. ESLint forbidden-vocab rule is OFF for this file via
   the override block in eslint.config.js — see plan critique #10.
   ============================================================ */

import type { TaskFamilyBand, TaskFamilyOutcome } from "@/types";

export type RiskBand = "low" | "moderate" | "high" | "more_info";
export type RiskOrUnknown = RiskBand | "unknown";
export type CompletionStatus = "completed" | "partial" | "notstarted";

export const RISK = {
  /** Default vocabulary — performance bands describe cohort position
   *  rather than predicted intervention need. The at-risk label set is
   *  retained below as a fallback constant for tests / historical
   *  reference, but is not surfaced in any teacher-facing UI because
   *  the at-risk classification has not yet been validated against
   *  external-criterion data. See docs/evidence-aware-psychometric-
   *  position-v1.md. */
  vocabulary: "performance" as "at-risk" | "performance",

  labels: {
    "at-risk": {
      low: "Low risk",
      moderate: "Moderate risk",
      high: "High risk",
      more_info: "No classification",
    },
    performance: {
      low: "On Track",
      moderate: "Low",
      high: "Very Low",
      more_info: "No classification",
    },
  },

  /* Forbidden in teacher-facing strings. The ESLint `forbidden-vocab`
     rule (see eslint.config.js) enforces a subset of this list at lint
     time — entries here that aren't enforced are still useful as a
     code-review checklist. `SE` is intentionally documented here only
     (not enforced) because its 2-character form has too many
     false-positive contexts (acronyms, geographic abbreviations). */
  forbiddenWords: [
    /* original locked-plan list (Phase 7-followup) */
    "pass", "fail", "behind", "fine", "warning", "borderline",
    "low ability", "diagnose",
    "below expectation", "above expectation", "as expected",
    "probe", "subscore", "subscale",
    /* deficit-framing additions per the revised governance brief */
    "struggling", "weak at", "deficit", "failing", "at-risk",
    "inconclusive",
    /* IRT jargon — fine in audit logs / docs / this file, never in
       teacher-facing UI strings */
    "θ", "theta", "SE", "posterior", "residual",
  ],
} as const;

export const STATUS = {
  labels: {
    completed: "Screened",
    partial: "Partial",
    notstarted: "Not started",
  },
} as const;

/* Beta-signalling copy — consumed by the wordmark BETA pill and the
   dismissible beta-notice banner. Wording is deliberately generic: it
   conveys "still being developed" without naming specific features or
   the performance-versus-risk reporting rationale. `versionKey` also
   serves as the localStorage key; bump it on a major release to
   resurface the banner for everyone. */
export const BETA_NOTICE = {
  versionKey: "enssa.beta-notice-v0.1",
  lead: "ENSSA is in early access",
  body: "— we'd love your feedback at",
  feedbackEmail: "maths@cis.org.au",
  dismissLabel: "Dismiss beta notice",
} as const;

export function riskLabel(band: RiskBand): string {
  return RISK.labels[RISK.vocabulary][band];
}

/**
 * Domain-facing label for transition / aggregate contexts. The `unknown`
 * (transition-only, no signal in target term) and `more_info` (screened
 * but insufficient data) states both surface as "Unknown" in chart and
 * Sankey contexts so teachers don't have to distinguish between them at
 * the aggregate layer.
 */
export function domainRiskLabel(band: RiskOrUnknown): string {
  if (band === "unknown" || band === "more_info") return "No classification";
  return riskLabel(band);
}

export function statusLabel(status: CompletionStatus): string {
  return STATUS.labels[status];
}

export const PORTAL_TERMS = {
  task: "Subtest",
  taskPlural: "Subtests",
  performanceProfile: "Subtest profile",
  followUpArea: "Priority follow-up area",
  insufficient: "No classification",
  compositeScore: "ENSSA Standard Score",
  likelyRange: "Likely range",
} as const;

/* ── Task-family band vocabulary ──────────────────────────────
   Subtest bands use the SAME performance vocabulary as the global
   score bands (Very Low / Low / On Track at P15/P35), defined by
   reference so the two can never drift. The grey evidence states
   are subtest-only. */
export const TASK_BAND = {
  labels: {
    very_low: RISK.labels.performance.high,
    low: RISK.labels.performance.moderate,
    on_track: RISK.labels.performance.low,
    limited: "Too few responses",
    not_attempted: "Not attempted",
  },
} as const;

export function taskBandLabel(band: TaskFamilyBand): string {
  return TASK_BAND.labels[band];
}

/** Ordinal form of a number — e.g. 68 → "68th", 21 → "21st". */
export function ordinal(n: number): string {
  const v = Math.round(n);
  const mod100 = v % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${v}th`;
  switch (v % 10) {
    case 1: return `${v}st`;
    case 2: return `${v}nd`;
    case 3: return `${v}rd`;
    default: return `${v}th`;
  }
}

function isNumberLineOutcome(o: TaskFamilyOutcome): boolean {
  const id = o.taskId?.toLowerCase() ?? "";
  return id.startsWith("bnl") || id.startsWith("unl");
}

function continuousItemsCorrect(o: TaskFamilyOutcome): string {
  return (Math.round(o.itemsCorrect * 10) / 10).toFixed(1);
}

/** Muted "·"-joined evidence string for a single student's task family. */
export function formatTaskEvidence(o: TaskFamilyOutcome): string {
  if (o.band === "not_attempted") return "not attempted";
  if (!o.rawDetailsAvailable) return "response details not supplied";
  if (o.band === "limited") return "not enough completed items to compare";
  if (isNumberLineOutcome(o)) {
    return `${continuousItemsCorrect(o)}/${o.itemsAttempted} · ${Math.round(o.accuracyPct)}% accuracy`;
  }
  const parts = [
    `${o.itemsAttempted}/${o.itemsTotal} attempted`,
    `${Math.round(o.accuracyPct)}%`,
  ];
  if (o.timed && o.correctPerMin != null) {
    parts.push(`${(Math.round(o.correctPerMin * 10) / 10).toString()} cpm`);
  }
  return parts.join(" · ");
}

/* Evidence shape varies by subtest type:
   - number line (continuous scoring): items attempted + accuracy
   - timed: items correct/attempted + rate (accuracy is redundant)
   - untimed discrete: items correct/attempted only */
export function formatCompactTaskEvidence(o: TaskFamilyOutcome): string[] {
  if (o.band === "not_attempted") return ["No items attempted"];
  if (!o.rawDetailsAvailable) return ["Response details not supplied"];
  const lines: string[] = [];
  if (isNumberLineOutcome(o)) {
    lines.push(`${o.itemsAttempted} items attempted`);
    lines.push(`${Math.round(o.accuracyPct)}% accuracy`);
  } else {
    lines.push(`${o.itemsCorrect}/${o.itemsAttempted} items correct`);
    if (o.timed && o.correctPerMin != null) {
      lines.push(`${(Math.round(o.correctPerMin * 10) / 10).toFixed(1)} correct/min`);
    }
  }
  if (o.band === "limited") lines.push("Too few responses");
  return lines;
}

export const DEMOGRAPHIC_LABELS = {
  sex: { all: "All", F: "Female", M: "Male" },
  atsi: {
    all: "All",
    yes: "Aboriginal & Torres Strait Islander",
    no: "Non-Indigenous",
    prefer_not_to_say: "Prefer not to say",
  },
  lbote: {
    all: "All",
    lbote: "Language background other than English",
    non_lbote: "English background",
  },
} as const;
